import { computed, makeObservable } from 'mobx';

import { IRootStore } from 'shared/entities/store/rootStore';
import { NotConnectedCloudPaymentsMethod } from 'shared/models/cloudPayments';
import ListModel from 'shared/models/ListModel';
import { ComponentLoadingStore } from 'stores/componentLoadingStore';
import {
  ICommonUserPaymentMethodModel,
  UserPaymentMethodKind
} from 'shared/entities/userPayment';
import { CommonUserPaymentMethodModel } from 'shared/models/userPayment';
import { FieldModel } from 'shared/models/form';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { apiUrls } from 'shared/entities/domain';

export default class CloudPaymentsStore extends ComponentLoadingStore {
  readonly notConnectedMethods: ListModel<NotConnectedCloudPaymentsMethod> =
    new ListModel<NotConnectedCloudPaymentsMethod>();
  readonly callbackUrl: FieldModel<string | null> = new FieldModel<
    string | null
  >(null);
  readonly gettingCallbackUrlStage: LoadingStageModel = new LoadingStageModel();
  private rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    super();
    this.rootStore = rootStore;

    makeObservable(this, {
      entitiesLength: computed
    });
  }

  get loaded(): boolean {
    return true;
  }

  get canBeLoaded(): boolean {
    return true;
  }

  get methods(): ICommonUserPaymentMethodModel[] {
    return this.rootStore.integrationsStore.userPaymentsStore.methods.items.reduce(
      (acc: ICommonUserPaymentMethodModel[], method) => {
        if (method.providerKind === UserPaymentMethodKind.cloudPayments) {
          acc.push(method);
        }
        return acc;
      },
      []
    );
  }

  get entitiesLength(): number {
    return this.notConnectedMethods.length + this.methods.length;
  }

  getCallbackUrl = async (): Promise<BaseResponse> => {
    if (this.gettingCallbackUrlStage.isLoading || this.callbackUrl.value) {
      return {
        isError: true
      };
    }

    this.gettingCallbackUrlStage.loading();

    const response = await this.rootStore.networkStore.api<{
      callback_url: string;
    }>(apiUrls.OAUTH_CLOUDPAYMENTS_GET_CALLBACK_URL);

    if (response.isError) {
      this.gettingCallbackUrlStage.error();
    } else {
      this.callbackUrl.changeValue(response.data.callback_url);
      this.gettingCallbackUrlStage.success();
    }

    return response;
  };

  addMethod = (): void => {
    const site = NotConnectedCloudPaymentsMethod.fromDefaultParams(
      this.rootStore
    );

    this.notConnectedMethods.addEntity({
      entity: site,
      key: site.id
    });
  };

  removeMethod = (notConnectedTerminalId: string): void => {
    this.notConnectedMethods.removeEntity(notConnectedTerminalId);
  };

  createMethod = async (
    notConnectedMethodId: string
  ): Promise<BaseResponse> => {
    const site = this.notConnectedMethods.getEntity(notConnectedMethodId);

    if (!site) {
      return {
        isError: true
      };
    }

    const response = await site.create();

    if (!response.isError) {
      const method = CommonUserPaymentMethodModel.fromJson(
        response.data,
        this.rootStore
      );
      if (!method) {
        return {
          isError: true
        };
      }
      this.notConnectedMethods.removeEntity(notConnectedMethodId);
      this.rootStore.integrationsStore.userPaymentsStore.methods.addEntity({
        entity: method,
        key: method.id
      });

      return {
        isError: false
      };
    }

    return {
      isError: true
    };
  };

  async load(): Promise<BaseResponse> {
    return {
      isError: false
    };
  }

  reset(): void {
    this.notConnectedMethods.reset();
  }
}
